import { observer } from 'mobx-react-lite'
import { ReactElement, useState } from 'react'
import { Button } from 'app/components/ui/button'
import { PageScroll } from 'app/components/layout/PageScroll'
import { PageHeaderComponent } from 'app/components/page-header/PageHeaderComponent'
import { useFeatureToggles } from './useFeatureToggles'
import { ByFeatureView } from './ByFeatureView'
import { ByReleaseView } from './ByReleaseView'
import { ByTargetView } from './ByTargetView'
import { ToggleDefinitionModal } from './ToggleDefinitionModal'
import type { FeatureRow } from './featureToggleModel'
import { getToggleMetadata } from './featureToggleMetadata'

export const FeatureTogglesComponent = observer(function FeatureTogglesComponent(): ReactElement {
  const vm = useFeatureToggles()
  const [defModalOpen, setDefModalOpen] = useState(false)
  const [editingRow, setEditingRow] = useState<FeatureRow | null>(null)

  const openCreateModal = () => {
    setEditingRow(null)
    setDefModalOpen(true)
  }

  const openEditModal = (row: FeatureRow) => {
    setEditingRow(row)
    setDefModalOpen(true)
  }

  return (
    <>
      <PageScroll
        header={
          <>
            <PageHeaderComponent title="Feature Toggles" />

            <div className="mb-4 flex items-center justify-between gap-4">
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={vm.view === `feature` ? `secondary` : `tertiary`}
                  onClick={() => vm.setView(`feature`)}
                >
                  By Feature
                </Button>
                <Button
                  size="sm"
                  variant={vm.view === `target` ? `secondary` : `tertiary`}
                  onClick={() => vm.setView(`target`)}
                >
                  By Target
                </Button>
                <Button
                  size="sm"
                  variant={vm.view === `release` ? `secondary` : `tertiary`}
                  onClick={() => vm.setView(`release`)}
                >
                  By Release
                </Button>
              </div>
              <div className="flex items-center gap-2">
                {vm.view === `feature` && (
                  <input
                    className="w-64 rounded border border-default px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                    placeholder="Search toggles"
                    value={vm.search}
                    onChange={(e) => vm.setSearch(e.target.value)}
                  />
                )}
                {vm.view === `feature` && (
                  <Button size="sm" variant="secondary" onClick={openCreateModal}>
                    New toggle
                  </Button>
                )}
              </div>
            </div>
          </>
        }
      >
        {vm.loading ? (
          <div className="flex h-full items-center justify-center text-sm text-minimal">Loading feature toggles…</div>
        ) : vm.view === `feature` ? (
          <ByFeatureView
            groups={vm.featureGroups}
            onToggleGlobal={vm.onToggleGlobal}
            onSetOff={vm.onSetOff}
            onSetSpecific={vm.onSetSpecific}
            onAddOverrides={vm.onAddOverrides}
            onRemoveOverride={vm.onRemoveOverride}
            onEditToggle={openEditModal}
            onSetArchived={vm.onSetArchived}
          />
        ) : vm.view === `release` ? (
          <ByReleaseView
            groups={vm.releaseGroups}
            onToggleGlobal={vm.onToggleGlobal}
            onSetOff={vm.onSetOff}
            onSetSpecific={vm.onSetSpecific}
            onAddOverrides={vm.onAddOverrides}
            onRemoveOverride={vm.onRemoveOverride}
            onEditToggle={openEditModal}
            onGiveRelease={vm.onGiveRelease}
          />
        ) : (
          <ByTargetView
            target={vm.target}
            onSelectTarget={vm.setTarget}
            rows={vm.targetRows}
            onToggleTarget={vm.onToggleTarget}
          />
        )}
      </PageScroll>

      {vm.pendingGlobal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-overlay/50">
          <div className="w-full max-w-md rounded-lg bg-surface-raised p-6 shadow-xl">
            <h3 className="mb-4 text-lg font-semibold">Enable &quot;{vm.pendingGlobal.label}&quot; for everyone?</h3>
            <p className="mb-4 text-sm text-subtle">
              There are {vm.pendingGlobal.workspaceOverrides.length + vm.pendingGlobal.userOverrides.length} existing
              workspace/user override(s). They become redundant once this is on globally. Remove them?
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="tertiary" size="sm" onClick={vm.cancelGlobal}>
                Cancel
              </Button>
              <Button variant="secondary" size="sm" onClick={() => vm.confirmGlobal(false)}>
                Keep overrides
              </Button>
              <Button variant="danger" size="sm" onClick={() => vm.confirmGlobal(true)}>
                Remove overrides
              </Button>
            </div>
          </div>
        </div>
      )}

      {vm.pendingOff && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-overlay/50">
          <div className="w-full max-w-md rounded-lg bg-surface-raised p-6 shadow-xl">
            <h3 className="mb-4 text-lg font-semibold">Turn off &quot;{vm.pendingOff.label}&quot;?</h3>
            <p className="mb-4 text-sm text-subtle">
              This removes {vm.pendingOff.workspaceOverrides.length + vm.pendingOff.userOverrides.length} existing
              workspace/user assignment(s). Nobody will have this toggle.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="tertiary" size="sm" onClick={vm.cancelOff}>
                Cancel
              </Button>
              <Button variant="danger" size="sm" onClick={vm.confirmOff}>
                Turn off
              </Button>
            </div>
          </div>
        </div>
      )}

      <ToggleDefinitionModal
        open={defModalOpen}
        onOpenChange={setDefModalOpen}
        editing={editingRow}
        editingId={editingRow ? vm.definitionIdFor(editingRow.name) : undefined}
        onSave={vm.saveDefinition}
        release={editingRow ? vm.releaseOf(editingRow.name) : null}
        codeRelease={(editingRow && getToggleMetadata(editingRow.name)?.release) ?? null}
      />
    </>
  )
})
